import React, { useState } from 'react';
import { useSimulationStore } from '@/store/useSimulationStore';
import type { Task, TaskStatus } from '@/types/task';

const COLUMNS: { status: TaskStatus; label: string; color: string }[] = [
  { status: 'todo', label: 'À FAIRE', color: '#4a5568' },
  { status: 'in_progress', label: 'EN COURS', color: '#00E5FF' },
  { status: 'waiting_validation', label: 'À VALIDER', color: '#FFB300' },
  { status: 'done', label: 'TERMINÉ', color: '#00E676' },
];

const PRIORITY_COLORS = {
  critical: '#FF1744',
  high: '#FF6D00',
  medium: '#FFB300',
  low: '#4a5568',
};

const TYPE_ICONS: Record<string, string> = {
  content: '📄',
  seo: '◎',
  analytics: '📊',
  customer_service: '💬',
  catalogue: '📋',
  marketing: '📣',
  technical: '⚙',
  validation: '✓',
  research: '🔍',
};

const TaskCard: React.FC<{ task: Task; agent?: ReturnType<typeof useSimulationStore.getState>['agents'][0]; onApprove?: () => void; onReject?: () => void }> = ({ task, agent, onApprove, onReject }) => (
  <div className="bg-bg-elevated rounded border border-bg-border p-3 mb-2 hover:border-gray-600 transition-colors">
    <div className="flex items-start justify-between gap-2 mb-2">
      <div className="flex items-center gap-1.5">
        <span className="text-sm">{TYPE_ICONS[task.type] ?? '·'}</span>
        <span
          className="font-mono text-xs px-1.5 py-0.5 rounded"
          style={{ background: PRIORITY_COLORS[task.priority] + '20', color: PRIORITY_COLORS[task.priority] }}
        >
          {task.priority.toUpperCase()}
        </span>
      </div>
      {agent && (
        <span
          className="font-pixel flex-shrink-0 px-1.5 py-0.5 rounded text-xs"
          style={{ background: agent.color + '15', color: agent.color, fontSize: '5px' }}
        >
          {agent.codename}
        </span>
      )}
    </div>

    <p className="font-mono text-xs text-gray-200 leading-relaxed mb-2">{task.title}</p>

    {/* Barre de progression */}
    {task.status === 'in_progress' && (
      <div className="mb-2">
        <div className="flex justify-between font-mono text-xs text-gray-600 mb-0.5">
          <span>Progression</span>
          <span>{task.progress}%</span>
        </div>
        <div className="h-1 bg-gray-800 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full"
            style={{ width: `${task.progress}%`, background: agent ? agent.color : '#00E5FF' }}
          />
        </div>
      </div>
    )}

    {/* Tokens */}
    <div className="font-mono text-xs text-gray-600">
      {task.actualTokens > 0 ? `${(task.actualTokens / 1000).toFixed(1)}K` : '—'} /
      {(task.estimatedTokens / 1000).toFixed(1)}K tokens
    </div>

    {/* Boutons validation */}
    {task.status === 'waiting_validation' && onApprove && onReject && (
      <div className="flex gap-2 mt-2">
        <button onClick={onApprove} className="flex-1 py-1 rounded text-xs font-mono text-neon-green border border-neon-green/40 hover:bg-neon-green/10">
          ✓ Approuver
        </button>
        <button onClick={onReject} className="flex-1 py-1 rounded text-xs font-mono text-neon-red border border-neon-red/30 hover:bg-neon-red/10">
          ✗ Rejeter
        </button>
      </div>
    )}
  </div>
);

export const PipelineView: React.FC = () => {
  const tasks = useSimulationStore(s => s.tasks);
  const agents = useSimulationStore(s => s.agents);
  const { approveTask, rejectTask } = useSimulationStore();
  const [filterAgent, setFilterAgent] = useState<string>('all');

  const filteredTasks = filterAgent === 'all' ? tasks : tasks.filter(t => t.agentId === filterAgent);

  return (
    <div className="flex flex-col flex-1 overflow-hidden p-4 gap-4">
      {/* Filtres */}
      <div className="flex items-center gap-3">
        <span className="font-pixel text-xs text-gray-400">PIPELINE</span>
        <select
          value={filterAgent}
          onChange={e => setFilterAgent(e.target.value)}
          className="bg-bg-elevated border border-bg-border rounded px-2 py-1 font-mono text-xs text-gray-300"
        >
          <option value="all">Tous les agents</option>
          {agents.map(a => (
            <option key={a.id} value={a.id}>{a.codename} — {a.roleName}</option>
          ))}
        </select>
        <span className="font-mono text-xs text-gray-600">{filteredTasks.length} tâches</span>
      </div>

      {/* Kanban */}
      <div className="flex gap-4 flex-1 overflow-x-auto overflow-y-hidden">
        {COLUMNS.map(col => {
          const colTasks = filteredTasks.filter(t => t.status === col.status);
          return (
            <div key={col.status} className="flex flex-col flex-shrink-0 w-72">
              {/* Header colonne */}
              <div className="flex items-center justify-between mb-3 px-1">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full" style={{ background: col.color, boxShadow: `0 0 6px ${col.color}` }} />
                  <span className="font-pixel text-xs" style={{ color: col.color, fontSize: '7px' }}>{col.label}</span>
                </div>
                <span className="font-mono text-xs text-gray-600">{colTasks.length}</span>
              </div>

              {/* Tâches */}
              <div className="flex-1 overflow-y-auto scrollbar-thin">
                {colTasks.map(task => {
                  const agent = agents.find(a => a.id === task.agentId);
                  return (
                    <TaskCard
                      key={task.id}
                      task={task}
                      agent={agent}
                      onApprove={task.status === 'waiting_validation' ? () => approveTask(task.id) : undefined}
                      onReject={task.status === 'waiting_validation' ? () => rejectTask(task.id) : undefined}
                    />
                  );
                })}
                {colTasks.length === 0 && (
                  <div className="text-center py-8 text-gray-700 font-mono text-xs">Vide</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
